// Vendors
import { cookies } from '@openbank/cf-ui-architecture-core';
import { StorageCookiesEnumeration } from '@gruposantander/opbpay-ui-core-models';
// Constants
import {
  CHECKOUT_ORDER_INFO_FAILED_CUSTOM_ACTION,
  REDIRECT_RETIRES_FAILED_NO_TMX_CUSTOM_ACTION,
  TMX_PROFILE_COMPLETED_CUSTOM_ACTION,
  TMX_PROFILE_TIMEOUT_CUSTOM_ACTION,
  TMX_PROFILE_TIMEOUT_MS,
  TMX_SCRIPT_AND_REDIRECT_DATA_CUSTOM_ACTION,
  TMX_SCRIPT_AND_REDIRECT_DATA_SKIPPED,
  TMX_SCRIPT_REDIRECT_DATA_FAILED_CUSTOM_ACTION,
} from './constants/checkout.handlers.constants';
// Enumerations
import { AppErrorEnumeration } from 'enumerations/app-error.enumeration';
import { AppInformationNeedEnumeration } from 'enumerations/app-information-need.enumeration';
import { TmxProfileResultEnumeration } from './utils/enumerations/tmx-profile-result.enumeration';
// Event Handlers
import {
  customerAuthDataHasBeenLoaded,
  getClientDevice,
  hasCustomerName,
  noTMXLoadedAndNoMoreRetriesAllowed,
  noTMXLoadedAndNoRedirectDataSaved,
} from './utils/checkout.handlers.utils';
import { waitForTmxProfile } from './utils/wait-for-tmx-profile.utils';
// Types
import {
  CallRedirectDataRetryHandlerType,
  CheckoutBuilderHandlersType,
  CheckoutMethodsHandlersType,
  NotifyDeviceAndTmxHandlerType,
} from './types/checkout.handlers.type';
// Utilities
import {
  fetchCheckoutOrderDataResolver,
  saveRedirectDataResolver,
} from '../resolvers/checkout.resolver';
import { getAppDomain } from 'utils/domain.utils';

let RETRIES = 0;

const createTMXScript = (tmxId: string): void => {
  const script = document.createElement('script'),
    code = `window.rftmwdu.profile('${window.TMX_PROFILING_DOMAIN}', '${window.TMX_ORG_ID}', '${tmxId}')`;

  script.type = 'text/javascript';
  script.appendChild(document.createTextNode(code));
  document?.getElementsByTagName('head')?.item(0)?.appendChild(script);
};

const updateCheckoutInformationFlag =
  () =>
  (prevFlags: AppInformationNeedEnumeration[]): AppInformationNeedEnumeration[] => [
    ...prevFlags,
    AppInformationNeedEnumeration.CHECKOUT_DATA,
  ];

const callRedirectDataRetryHandler = async ({
  handleSendCustomActionEvent,
}: CallRedirectDataRetryHandlerType): Promise<void> => {
  const [response] = await saveRedirectDataResolver(getClientDevice());

  if (response) {
    await handleSendCustomActionEvent(TMX_SCRIPT_AND_REDIRECT_DATA_CUSTOM_ACTION);
  } else {
    RETRIES = RETRIES + 1;
    await handleSendCustomActionEvent(TMX_SCRIPT_REDIRECT_DATA_FAILED_CUSTOM_ACTION + RETRIES);
    await callRedirectDataRetryHandler({ handleSendCustomActionEvent });
  }
};

const createTmxScriptAndCallRedirectDataHandler = async ({
  handleSendCustomActionEvent,
  tmxId,
}: NotifyDeviceAndTmxHandlerType): Promise<void> => {
  createTMXScript(tmxId);

  const profileResult = await waitForTmxProfile({ timeoutMs: TMX_PROFILE_TIMEOUT_MS });

  if (profileResult === TmxProfileResultEnumeration.COMPLETED) {
    await handleSendCustomActionEvent(TMX_PROFILE_COMPLETED_CUSTOM_ACTION);
  } else {
    await handleSendCustomActionEvent(TMX_PROFILE_TIMEOUT_CUSTOM_ACTION);
  }

  await callRedirectDataRetryHandler({ handleSendCustomActionEvent });
};

const notifyDeviceAndTmxHandler = async ({
  handleSendCustomActionEvent,
  tmxId,
  tmxCallDone,
}: NotifyDeviceAndTmxHandlerType): Promise<void> => {
  switch (true) {
    case noTMXLoadedAndNoRedirectDataSaved({ tmxCallDone, retries: RETRIES }):
      await createTmxScriptAndCallRedirectDataHandler({
        handleSendCustomActionEvent,
        tmxId,
        tmxCallDone,
      });
      break;
    case noTMXLoadedAndNoMoreRetriesAllowed({ tmxCallDone, retries: RETRIES }):
      await handleSendCustomActionEvent(REDIRECT_RETIRES_FAILED_NO_TMX_CUSTOM_ACTION);
      createTMXScript(tmxId);
      break;
    default:
      await handleSendCustomActionEvent(TMX_SCRIPT_AND_REDIRECT_DATA_SKIPPED + tmxCallDone);
  }
};

const fetchCheckoutOrderDataHandler = async ({
  handleSendCustomActionEvent,
  informationFlags,
  setCallbackUrl,
  setCheckoutOrderData,
  setCustomerName,
  setInformationFlags,
  setError,
  setLoanId,
}: CheckoutBuilderHandlersType): Promise<void> => {
  if (customerAuthDataHasBeenLoaded(informationFlags)) {
    const [response] = await fetchCheckoutOrderDataResolver();

    if (response) {
      const {
        callbackUrl = '',
        customer: { firstName, lastName, phone },
        merchantOrderId,
        tmxId = '',
        tmxCallDone = false,
      } = response;

      await notifyDeviceAndTmxHandler({
        handleSendCustomActionEvent,
        tmxId,
        tmxCallDone,
      });

      cookies.setCookie({
        name: StorageCookiesEnumeration.USERNAME,
        value: phone,
        domain: getAppDomain(),
      });

      if (callbackUrl) setCallbackUrl(callbackUrl);
      if (merchantOrderId) setLoanId(merchantOrderId);
      if (hasCustomerName({ firstName, lastName })) setCustomerName(`${firstName} ${lastName}`);

      setCheckoutOrderData(response);
      setInformationFlags(updateCheckoutInformationFlag());
    } else {
      handleSendCustomActionEvent(CHECKOUT_ORDER_INFO_FAILED_CUSTOM_ACTION);
      setError(AppErrorEnumeration.CHECKOUT_NO_INFO);
    }
  }
};

const CheckoutHandlers = (props: CheckoutBuilderHandlersType): CheckoutMethodsHandlersType => ({
  handleFetchCheckoutOrderData: () => fetchCheckoutOrderDataHandler(props),
});

export { CheckoutHandlers };
